import { Button, ModalV2, TextArea } from '@components'
import { RadioItem } from '@components/OnboardingRadioList'
import { useBusinessContext } from '@context'
import { BusinessOnboardingStepKey } from '@query'
import { completeOnboardStep } from '@query/onboard'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import React from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-hot-toast'
import {
  HearAboutSourceKey,
  getHearAboutDetailPlaceholderKey,
  getShuffledHearAboutSources,
  hearAboutSelectionNeedsText
} from './hearAboutUsSources'
import { HearAboutRadioList, StepSection } from './HearAboutUsModal.styled'

interface Props {
  isVisible: boolean
  stepId: number
  creditReward: number
  onClose: () => void
  onResolved: (groupKey: BusinessOnboardingStepKey, kind: 'done' | 'skipped') => void
}

interface HearAboutUsFormValues {
  sources: HearAboutSourceKey[]
  detailText: string
}

const defaultValues: HearAboutUsFormValues = {
  sources: [],
  detailText: ''
}

const FORM_ID = 'hear-about-us-form'

export const HearAboutUsModal: React.FC<Props> = ({
  isVisible,
  stepId,
  creditReward,
  onClose,
  onResolved
}) => {
  const [t] = useTranslation()
  const { slug } = useBusinessContext()
  const queryClient = useQueryClient()
  const sourceOrderRef = React.useRef(getShuffledHearAboutSources())
  const {
    control,
    register,
    reset,
    handleSubmit,
    formState: { errors }
  } = useForm<HearAboutUsFormValues>({
    defaultValues
  })
  const selectedSources = useWatch({ control, name: 'sources' }) ?? []
  const detailText = useWatch({ control, name: 'detailText' }) ?? ''

  const closeAndReset = () => {
    reset(defaultValues)
    onClose()
  }

  const { mutateAsync: submitAnswer, isPending: isSubmitting } = useMutation({
    mutationFn: ({ sources, detailText: details }: HearAboutUsFormValues) =>
      completeOnboardStep({
        slug,
        stepId,
        sources,
        detail_text: details.trim() || undefined
      }),
    onError: () => toast.error(t('general.error')),
    onSuccess: async () => {
      onResolved(BusinessOnboardingStepKey.HEAR_ABOUT_US, 'done')
      await queryClient.refetchQueries({ queryKey: [slug] })
      closeAndReset()
    }
  })

  const reasonNeedsText = hearAboutSelectionNeedsText(selectedSources)
  const showDetailsField = selectedSources.length > 0
  const detailPlaceholderKey = getHearAboutDetailPlaceholderKey(selectedSources)
  const canSubmit = selectedSources.length > 0 && (!reasonNeedsText || detailText.trim().length > 0)

  return (
    <ModalV2
      isVisible={isVisible}
      handleOnClose={closeAndReset}
      header={t('dashboard.getStartedWidget.hearAboutUs.title')}
      width={600}
      height={760}
      autoHeight
      footer={
        <>
          <Button type="button" isSecondary onClick={closeAndReset} disabled={isSubmitting}>
            {t('general.cancel')}
          </Button>
          <Button
            type="submit"
            form={FORM_ID}
            intent="primary"
            showSpinner={isSubmitting}
            disabled={!canSubmit || isSubmitting}
          >
            {t('dashboard.getStartedWidget.hearAboutUs.submit', { credits: creditReward })}
          </Button>
        </>
      }
    >
      <form id={FORM_ID} onSubmit={handleSubmit(values => submitAnswer(values))}>
        <StepSection>
          <p>{t('dashboard.getStartedWidget.hearAboutUs.description')}</p>
          <Controller
            control={control}
            name="sources"
            rules={{ validate: value => value.length > 0 }}
            render={({ field: { value, onChange } }) => (
              <HearAboutRadioList
                role="group"
                aria-label={t('dashboard.getStartedWidget.hearAboutUs.title')}
              >
                {sourceOrderRef.current.map((source, index) => (
                  <RadioItem
                    key={source}
                    label={t(`dashboard.getStartedWidget.hearAboutUs.options.${source}`)}
                    index={index}
                    selected={value.includes(source)}
                    value={source}
                    onSelect={selectedValue => {
                      if (value.includes(selectedValue)) {
                        onChange(value.filter(selected => selected !== selectedValue))
                        return
                      }
                      onChange([...value, selectedValue])
                    }}
                  />
                ))}
              </HearAboutRadioList>
            )}
          />

          {showDetailsField && (
            <TextArea
              {...register('detailText', {
                validate: value =>
                  !reasonNeedsText || value.trim().length > 0 || t('validation.required')
              })}
              errors={errors.detailText}
              label={t('dashboard.getStartedWidget.hearAboutUs.detailsLabel')}
              placeholder={t(
                `dashboard.getStartedWidget.hearAboutUs.detailPlaceholder.${detailPlaceholderKey}`
              )}
              required={reasonNeedsText}
              rows={3}
              aria-required={reasonNeedsText}
            />
          )}
        </StepSection>
      </form>
    </ModalV2>
  )
}
